import { Phone, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { BUSINESS, telLink, waLink } from "../lib/business";

// TikTok glyph (lucide doesn't provide one)
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
  </svg>
);

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative pt-16 pb-10 bg-[#020617] border-t border-slate-800/60"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.35)]">
                <svg
                  viewBox="0 0 24 24"
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              </div>
              <div className="leading-tight">
                <div className="text-base font-bold text-white">
                  {BUSINESS.nameBrand}
                </div>
                <div className="text-xs text-cyan-400/80 font-medium">
                  {BUSINESS.tagline}
                </div>
              </div>
            </div>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-md">
              {BUSINESS.nameFull} — ورشة متخصصة في تكييف وكهرباء السيارات في
              الرياض، حي الخليج. تشخيص دقيق، خدمة محترمة، ومصداقية يشهد لها
              العملاء.
            </p>

            <div className="mt-6 flex gap-3">
              <a
                href={telLink}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold transition"
              >
                <Phone className="w-4 h-4" />
                اتصل الآن
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#25D366] hover:bg-[#1DA851] text-white text-sm font-semibold transition"
              >
                <MessageCircle className="w-4 h-4" />
                واتساب
              </a>
            </div>

            {/* Social links */}
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">
                تابعنا
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={BUSINESS.socials.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-tiktok-link"
                  aria-label={`تيك توك ${BUSINESS.socials.tiktokHandle}`}
                  className="group inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-800 hover:border-fuchsia-500/50 bg-slate-900/60 transition-all"
                >
                  <TikTokIcon className="w-4 h-4 text-slate-300 group-hover:text-fuchsia-400 transition-colors" />
                  <span className="text-xs font-medium text-slate-300 group-hover:text-white">
                    تيك توك
                  </span>
                </a>
                <a
                  href={BUSINESS.socials.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-telegram-link"
                  aria-label={`تيليجرام ${BUSINESS.socials.telegramHandle}`}
                  className="group inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-800 hover:border-sky-500/50 bg-slate-900/60 transition-all"
                >
                  <Send className="w-4 h-4 text-slate-300 group-hover:text-sky-400 transition-colors" />
                  <span className="text-xs font-medium text-slate-300 group-hover:text-white">
                    تيليجرام
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Services links */}
          <div className="lg:col-span-3">
            <div className="text-sm font-semibold text-white mb-4">خدماتنا</div>
            <ul className="space-y-2.5 text-sm text-slate-400">
              <li>
                <a href="#services" className="hover:text-cyan-400 transition">
                  فحص وتشخيص أعطال المكيف
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-cyan-400 transition">
                  تعبئة فريون
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-cyan-400 transition">
                  ترهيم مكيفات السيارات
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-cyan-400 transition">
                  تغيير ثلاجة المكيف
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-cyan-400 transition">
                  إصلاح كمبروسر المكيف
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-cyan-400 transition">
                  إصلاح كهرباء السيارات
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <div className="text-sm font-semibold text-white mb-4">
              تواصل معنا
            </div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-4.5 h-4.5 text-cyan-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300 leading-relaxed">
                  {BUSINESS.address}
                  <br />
                  <span className="text-slate-500 text-xs">
                    Plus Code: {BUSINESS.plusCode}
                  </span>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4.5 h-4.5 text-cyan-400" />
                <a
                  href={telLink}
                  className="text-slate-300 hover:text-cyan-400 tabular-nums transition"
                >
                  {BUSINESS.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4.5 h-4.5 text-cyan-400" />
                <span className="text-slate-300">{BUSINESS.openingHours}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* SEO footer keywords */}
        <div className="mt-12 pt-8 border-t border-slate-800/60 text-[11px] text-slate-600 leading-relaxed">
          كهربائي سيارات الرياض · كهربائي مكيف سيارات الرياض · اصلاح مكيف سيارة
          الرياض · تعبئة فريون سيارات الرياض · ترهيم مكيف سيارات · تغيير ثلاجة
          مكيف السيارة · ورشة تكييف سيارات الرياض · اصلاح كهرباء السيارات
          الرياض · صيانة مكيف جميع السيارات · كهربائي سيارات الخليج الرياض ·
          ورشة كهرباء وتكييف سيارات حي الخليج · افضل كهربائي سيارات في الرياض ·
          متخصص تكييف سيارات قريب مني · فحص أعطال مكيف السيارة الرياض ·
          إصلاح كمبروسر مكيف سيارة · صيانة كهرباء سيارات يابانية وأمريكية
          وأوروبية وكورية
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            © {new Date().getFullYear()} {BUSINESS.nameFull}. جميع الحقوق محفوظة.
          </div>
          <div>
            تم التصميم بعناية لعملاء الرياض
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
